import { apiClient } from '@/utils/api-client'
import { cleanParams } from '@/utils/clean-params'

const API_INTERNAL_BASE = '/audit'

/**
 * `responseType: 'blob'` requests never get their error body parsed as JSON
 * by axios (or by the shared `parseErrors` interceptor), so a 422 validation
 * error surfaces as a generic "Request failed with status code 422" instead
 * of the actual message. Re-derive the real message from the blob here.
 *
 * @param {Error} error
 * @returns {Promise<Error>}
 */
async function toReadableExportError(error) {
    const blob = error.response?.data
    if (!(blob instanceof Blob)) {
        return error
    }

    try {
        const parsed = JSON.parse(await blob.text())
        const fieldMessage = Object.values(parsed.errors ?? {})[0]?.[0]
        const message = fieldMessage || parsed.message

        return message ? new Error(message) : error
    } catch {
        return error
    }
}

class AuditService {
    /**
     * Fetch audit events with optional filters and pagination.
     *
     * @param {Object} params - Query parameters
     * @param {string} [params.actor_ulid] - Filter by actor ULID
     * @param {string} [params.flag_ulid] - Filter by flag, across flag-subject and target-publish events
     * @param {string} [params.action] - Filter by action (e.g. 'flag.updated')
     * @param {string} [params.subject_type] - Filter by subject type
     * @param {string} [params.subject_ulid] - Filter by subject ULID
     * @param {string} [params.scope_project_ulid] - Filter by project scope
     * @param {string} [params.scope_environment_ulid] - Filter by environment scope
     * @param {string} [params.created_from] - ISO-8601 start date
     * @param {string} [params.created_to] - ISO-8601 end date
     * @param {number} [params.limit] - Page size (1-200, default 50)
     * @param {string} [params.cursor] - Pagination cursor
     * @param {string} [params.sort] - Sort order: 'created_at_desc' or 'created_at_asc'
     * @param {boolean} [params.export] - Export mode
     * @param {Object} [options] - Request options
     * @param {AbortSignal} [options.signal] - Signal to cancel this request
     * @returns {Promise<Object>} Response with items[], meta
     */
    async list(params = {}, { signal } = {}) {
        return await apiClient
            .get(API_INTERNAL_BASE + '/events', {
                params: cleanParams(params),
                signal,
            })
            .then((response) => response.data)
    }

    /**
     * Download the filtered audit log as a CSV file for compliance export.
     *
     * The API requires an explicit `created_from`/`created_to` range (max 180 days).
     *
     * @param {Object} params - Same filters as `list`, minus pagination
     * @returns {Promise<Blob>} CSV file contents
     */
    async exportCsv(params = {}) {
        return await apiClient
            .get(API_INTERNAL_BASE + '/events/export', {
                params: cleanParams(params),
                responseType: 'blob',
            })
            .then((response) => response.data)
            .catch(async (error) => {
                throw await toReadableExportError(error)
            })
    }

    /**
     * Fetch the per-user activity summary report.
     *
     * @param {Object} params - Query parameters
     * @param {string} [params.actor_ulid] - Filter to a single user
     * @param {string} [params.team_ulid] - Filter to members of a team
     * @param {string} [params.created_from] - ISO-8601 start date (defaults to 30 days back)
     * @param {string} [params.created_to] - ISO-8601 end date (defaults to now)
     * @param {string} [params.sort_by] - 'total_actions' or 'name'
     * @param {string} [params.sort_dir] - 'asc' or 'desc'
     * @param {Object} [options] - Request options
     * @param {AbortSignal} [options.signal] - Signal to cancel this request
     * @returns {Promise<Object>} Response with filters, summary, actors[]
     */
    async getUserActivity(params = {}, { signal } = {}) {
        return await apiClient
            .get(API_INTERNAL_BASE + '/user-activity', {
                params: cleanParams(params),
                signal,
            })
            .then((response) => response.data)
    }
}

export default new AuditService()
